/* Best-effort Instagram handoff. Private app schemes may be blocked or changed.
 * Keep the storefront available if the handoff is ignored, and never retry
 * throughout the same in-app session (including navigation and back/refresh).
 * Instagram owns the external-navigation prompt; this cannot suppress it.
 */
(() => {
  const ua = navigator.userAgent || '';
  if (!/Instagram/i.test(ua) || window.Shopify?.designMode) return;
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  if (!isIOS && !isAndroid) return;

  const url = new URL(window.location.href);
  if (url.protocol !== 'https:') return;
  // Avoid interrupting form results, customer authentication or payment flows.
  if (/\/(?:account|cart|checkout|checkouts|challenge)(?:\/|$)/i.test(url.pathname)
      || url.searchParams.has('customer_posted')
      || url.searchParams.has('contact_posted')
      || url.searchParams.has('preview_theme_id')
      || url.searchParams.has('preview_token')) return;

  // Returning through history must not launch an app, even if this WebView
  // restored the page without its original session storage.
  const navigation = window.performance?.getEntriesByType?.('navigation')[0];
  if (navigation?.type === 'back_forward') return;

  // Protect against duplicate script inclusion before a hidden page activates.
  const stateKey = '__mlfInstagramHandoff';
  if (window[stateKey]) return;
  window[stateKey] = true;

  const key = 'mlf:instagram-browser-handoff';
  let refresh;
  let cleanupTimer;
  let stopped = false;

  const cleanup = () => {
    stopped = true;
    document.removeEventListener('visibilitychange', activate);
    document.removeEventListener('prerenderingchange', activate);
    window.removeEventListener('pagehide', cleanup);
    clearTimeout(cleanupTimer);
    refresh?.remove();
  };

  function activate() {
    if (stopped || document.prerendering || document.visibilityState === 'hidden') return;
    document.removeEventListener('visibilitychange', activate);
    document.removeEventListener('prerenderingchange', activate);
    try {
      if (sessionStorage.getItem(key)) {
        cleanup();
        return;
      }
      // Persist BEFORE navigating: a blocked attempt still counts as the one
      // attempt. Never retry on a timer, a customer tap, or a return from Safari.
      sessionStorage.setItem(key, '1');
    } catch {
      // Without a persistent guard, don't risk repeated launches on navigation.
      cleanup();
      return;
    }

    try {
      launch();
    } catch {
      cleanup();
    }
  }

  function launch() {
    if (isIOS) {
      // Ask Instagram to hand this exact URL to the external browser. A meta
      // refresh is used because some versions reject scripted scheme navigation.
      refresh = document.createElement('meta');
      refresh.httpEquiv = 'refresh';
      refresh.content = '0;url=instagram://extbrowser/?url=' + encodeURIComponent(url.href);
      document.head.appendChild(refresh);
      cleanupTimer = setTimeout(cleanup, 1500);
    } else {
      // No Chrome package: let Android resolve the user's browser preference.
      // Encode the fragment so it cannot be interpreted as intent directives.
      const destination = url.host + url.pathname + url.search
        + (url.hash ? '%23' + encodeURIComponent(url.hash.slice(1)) : '');
      const intent = 'intent://' + destination
        + '#Intent;scheme=https;action=android.intent.action.VIEW;'
        + 'category=android.intent.category.BROWSABLE;'
        + 'S.browser_fallback_url=' + encodeURIComponent(url.href) + ';end';
      window.location.replace(intent);
      cleanup();
    }
  }

  // Instagram may prepare a page in the background. Wait until it is actually
  // visible before consuming its single attempt, and cancel if it is discarded.
  document.addEventListener('visibilitychange', activate);
  document.addEventListener('prerenderingchange', activate);
  window.addEventListener('pagehide', cleanup);
  activate();
})();
